import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  inject,
  Input,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ModalExhibitorComponent } from '@base/components/modal-exhibitor/modal-exhibitor.component';
import { GlobalState } from '@base/models/others/global-state';
import { SearchReason } from '@base/models/others/match-reason';
import { InformaMfeMatchContext } from '@base/models/others/informa-mfe-event';
import { ExhibitorDetail } from '@base/models/responses/exhibitor-detail.res';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { GlobalService } from '@base/services/private/global.service';
import { MeetService } from '@base/services/private/meet.service';
import { SeoService } from '@base/services/private/seo.service';
import { WhiteLabel } from '@config/whitelabel-keys.constant';

@Component({
  selector: 'app-exhibitor-detail',
  imports: [],
  templateUrl: './exhibitor-detail.component.html',
  styleUrl: './exhibitor-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExhibitorDetailComponent implements AfterViewInit {
  @ViewChild('exhibitorDetailContainer', { read: ViewContainerRef }) exhibitorDetailContainer!: ViewContainerRef;
  @Input() modalId = '';
  @Input() exhibitorId: string | null = null;
  // The detail API never returns search_reasons - it only exists on list/search responses -
  // so the caller carries it in from the list item that was clicked.
  @Input() searchReasons: SearchReason[] | null = null;
  @Input() matchContext: InformaMfeMatchContext | null = null;
  @Input() overlayFeedback: (() => void) | null = null;
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly meetSrv = inject(MeetService);
  private readonly seoSrv = inject(SeoService);
  private readonly globalSrv = inject(GlobalService);
  exhibitorDetailInstance: ComponentRef<unknown> | undefined;
  exhibitorDetail: ExhibitorDetail | null = null;
  isShowPlaceholder = true;
  globalState: GlobalState | null = null;

  ngAfterViewInit(): void {
    (async () => {
      await this.loadExhibitorDetailComponent();
    })();
  }

  /**
   * Fetches the exhibitor detail from the server using the provided `exhibitorId`.
   * Updates the state and triggers rendering of the detail component.
   *
   * @returns void
   */
  getExhibitorDetail(): void {
    if (!this.exhibitorId) return;

    this.meetSrv.getExhibitorDetail(this.exhibitorId).subscribe({
      next: (res) => {
        if (!res) {
          this.globalSrv.destroyModalRef(this.modalId);
          return;
        }
        this.isShowPlaceholder = false;
        // Detail responses normally omit search reasons. The clicked list item passes them in.
        this.exhibitorDetail = {
          ...res,
          search_reasons: this.searchReasons ?? res.search_reasons,
        };
        this.renderExhibitorDetail();
        this.setupExhibitorDetailMetaTags();
      },
      error: (err) => {
        console.error('Error fetching exhibitor detail:', err);
        this.isShowPlaceholder = false;
        this.globalSrv.destroyModalRef(this.modalId);
      },
    });
  }

  /**
   * Sets up SEO meta tags for the exhibitor detail page.
   */
  setupExhibitorDetailMetaTags() {
    if (!this.exhibitorDetail) return;

    const metaTagObjectArray = [
      {
        property: 'og:url',
        content: String(window.location.href),
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:title',
        content: String(this.exhibitorDetail?.company_name ?? ''),
      },
      {
        property: 'og:description',
        content: String(this.exhibitorDetail?.description ?? ''),
      },
      {
        property: 'og:image',
        content: String(this.exhibitorDetail?.company_banner ?? ''),
      },
    ];

    this.seoSrv.updateMetaTags(metaTagObjectArray);
  }

  /**
   * Dynamically loads the `ModalExhibitor` component into the container.
   *
   * @returns A Promise that resolves when the component is loaded successfully.
   */
  async loadExhibitorDetailComponent(): Promise<void> {
    try {
      const component = await this.dynamicImportLoaderSrv.loadComponent(
        this.exhibitorDetailContainer,
        WhiteLabel.COMPONENTS.ModalExhibitor
      );
      if (component) {
        this.exhibitorDetailInstance = component.componentRef || undefined;
        if (this.matchContext) {
          this.exhibitorDetailInstance?.setInput('matchContext', this.matchContext);
        }
        if (this.overlayFeedback) {
          this.exhibitorDetailInstance?.setInput('overlayFeedback', this.overlayFeedback);
        }
        this.getExhibitorDetail();
      } else {
        throw new Error('Components could not be loaded.');
      }
    } catch (error) {
      console.error('Failed to load components:', error);
    }
  }

  /**
   * Passes input values to the loaded `ModalExhibitorComponent` instance.
   *
   * @returns void
   */
  renderExhibitorDetail(): void {
    const component = this.exhibitorDetailInstance?.instance as ModalExhibitorComponent;
    if (component) {
      this.exhibitorDetailInstance?.setInput('exhibitorId', this.exhibitorId);
      this.exhibitorDetailInstance?.setInput('exhibitorDetail', this.exhibitorDetail);
      this.exhibitorDetailInstance?.setInput('isShowPlaceholder', this.isShowPlaceholder);
      this.exhibitorDetailInstance?.setInput('modalId', this.modalId);
      if (this.matchContext) {
        this.exhibitorDetailInstance?.setInput('matchContext', this.matchContext);
      }
      if (this.overlayFeedback) {
        this.exhibitorDetailInstance?.setInput('overlayFeedback', this.overlayFeedback);
      }
    }
  }
}
